import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { queryKeys } from '../client/query-client';
/**
 * The Journey (spec §5).
 *
 * **Nothing here is a stored achievement.** A badge is earned exactly when
 * every module of its course is complete, a streak is the run of days in
 * `challenge_entries`, and the score is a function of five counts — all three
 * computed on read. There is deliberately no `awarded` table, because a stored
 * grant needs a writer to fire at the right moment and disagrees with reality
 * when it does not.
 *
 * So this client never derives any of it. It renders what the server counted.
 */
export const StreakTier = z.enum(['NONE', 'SEED', 'SPROUT', 'BLOOM', 'EVERGREEN']);
export const Streak = z.object({
    current: z.number().int().nonnegative(),
    longest: z.number().int().nonnegative(),
    tier: StreakTier,
    /** Today not being logged does NOT break a streak — the day is not over. */
    loggedToday: z.boolean(),
});
export const Badge = z.object({
    id: z.string().uuid(),
    key: z.string(),
    name: z.string(),
    description: z.string().optional(),
    courseId: z.string().uuid().nullable(),
    /** Computed: every module of the course complete. Never stored. */
    earned: z.boolean(),
    /**
     * The course's cover — the wall is one badge per course the member may
     * take, named after it (14 Sep 2026). Null for a course with no cover.
     */
    artworkAssetId: z.string().nullable(),
    /** A signed URL for that cover; the client dims it while the badge is locked. */
    artworkUrl: z.string().nullable(),
    modulesTotal: z.number().int().nonnegative(),
    modulesComplete: z.number().int().nonnegative(),
    /**
     * When the badge was earned — null until it is.
     *
     * DERIVED, like `earned` itself, from the newest `completedAt` among the
     * course's modules rather than stored at a grant. One consequence, stated so
     * it is not discovered: a course that gains a twelfth module UN-earns its
     * badge, and this date goes with it. That is the behaviour `earned` has
     * always had.
     */
    earnedOn: z.string().datetime().nullable().optional(),
});
export const Journey = z.object({
    score: z.number().int().min(0).max(100),
    breakdown: z.array(z.object({ key: z.string(), earned: z.number().int(), weight: z.number().int() })),
    daysAboard: z.number().int().nonnegative(),
    coursesFinished: z.number().int().nonnegative(),
    readingsFinished: z.number().int().nonnegative(),
    minutesListened: z.number().int().nonnegative(),
    /**
     * The four counters the constellation names.
     *
     * `modulesFinished` comes off the same table a badge derives from, so the
     * tile and the badge wall cannot disagree. `communityPosts` counts POSTS, not
     * comments — folding replies in makes a number nothing on the community
     * surface reproduces. `anchorsSaved` is the same union `/me/saved` lists, so
     * the tile and the list agree by construction.
     */
    modulesFinished: z.number().int().nonnegative().optional(),
    communityPosts: z.number().int().nonnegative().optional(),
    anchorsSaved: z.number().int().nonnegative().optional(),
    stormsWeathered: z.number().int().nonnegative().optional(),
    streak: Streak,
    badges: z.array(Badge),
});
/**
 * The API wraps this one — `{ journey, locale }`, not a bare `Journey`.
 *
 * ── How this got missed ───────────────────────────────────────────────────
 * `localisedOf` models the LIST envelope (`{ items, locale }`) and its own
 * comment warns there are three and they are not interchangeable. This is the
 * named-single-object one, and nothing modelled it — so the schema was pointed
 * at the envelope and rejected it, the query never resolved, and the journey
 * ring and days-aboard line rendered as skeletons forever.
 *
 * It passed every local run because the mock returns the INNER object. That is
 * the whole failure mode: a hand-written fixture agreeing with a hand-written
 * schema proves only that they agree with each other.
 *
 * `.transform` unwraps here so callers keep receiving a `Journey` and nothing
 * downstream had to change.
 */
const JourneyEnvelope = z
    .object({ journey: Journey, locale: z.string() })
    .transform((response) => response.journey);
export function useJourney(http) {
    return useQuery({
        queryKey: queryKeys.journey.root,
        queryFn: () => http.get('/journey', { schema: JourneyEnvelope }),
    });
}
// ── The weekly challenge ────────────────────────────────────────────────────
export const ChallengeDay = z.object({
    day: z.string(),
    logged: z.boolean(),
    note: z.string().nullable(),
    /**
     * The line for this day: its own prompt where the editor wrote one, else the
     * period's daily prompt — resolved by the server. It was parsed away, so a
     * week written day by day showed parents the same line all seven days.
     */
    prompt: z.string().nullable().optional(),
    state: z.enum(['done', 'now', 'upcoming', 'missed']),
});
export const ChallengePeriod = z.object({
    id: z.string().uuid(),
    kind: z.enum(['WEEK', 'MONTH']),
    startsOn: z.string(),
    endsOn: z.string(),
    title: z.string(),
    /**
     * The paragraph under the title on the home card — in the contract, and
     * parsed away here until now, so the card ran the daily prompt in the
     * description's slot AND again inside the "For today" panel.
     */
    description: z.string().nullable().optional(),
    theme: z.string().nullable().optional(),
    dailyPrompt: z.string().optional(),
    /** The end-of-period question. Not the daily one. */
    reflectionPrompt: z.string().nullable().optional(),
    /** Granted after round 3 §3 — the 2–4 suggested practices the design shows. */
    practices: z.array(z.string()).optional(),
    days: z.array(ChallengeDay),
    streak: Streak,
    /**
     * Signed URL for the challenge's cover artwork. Nullable — a period without
     * imagery renders as a plain card and the daily prompt does the work.
     */
    coverUrl: z.string().nullable(),
});
/**
 * `/journey/challenge` — `{ period, locale }`, and `period` is nullable.
 *
 * ── We got this wrong in both directions, so it is written down ───────────
 * The generated document declared a bare `ChallengePeriod`. Prose in a round-14
 * reply described the same thing. On that pair of signals we moved our fixture
 * to the bare shape and prepared to collapse a tolerant schema down to it.
 *
 * Both were wrong. The backend drove the route: the top-level keys are
 * `["period","locale"]`. The one signal we had discounted as "the odd one out"
 * — the route description saying *"`period` is null between periods"* — was the
 * only accurate thing in the set, and it was accurate because it described the
 * WIRE while the schema described a decorator's default.
 *
 * The lesson worth keeping: when a document contradicts itself, the half that
 * names a field is evidence and the half that omits one is silence. We treated
 * silence as the stronger signal because there was more of it.
 *
 * `null` between periods is a normal state, not an error — hence `.nullable()`
 * rather than a schema that throws on the quiet week of the year.
 */
const ChallengeEnvelope = z
    .object({ period: ChallengePeriod.nullable(), locale: z.string() })
    .transform((response) => response.period);
/**
 * `locale` is the language the challenge's text is read in. It was not sent, so
 * the text followed the browser's own Accept-Language — an English browser read
 * the French member's week in English — and the cache did not change with the
 * language switch (23 Sep 2026). Keyed by it, so switching refetches.
 */
export function useChallenge(http, locale) {
    return useQuery({
        queryKey: [...queryKeys.journey.challenge, locale ?? 'default'],
        queryFn: () => http.get('/journey/challenge', { schema: ChallengeEnvelope, ...(locale ? { locale } : {}) }),
        retry: false,
        /*
         * `coverUrl` is a signed URL with a 5-minute TTL — the response goes
         * stale from the browser's point of view before the *query* is stale
         * by any React Query default, so the home page keeps rendering a
         * link that answers 403. Refresh a minute before expiry so the URL
         * on screen is always fresh. Same seam every other timed URL should
         * follow when it ends up on a long-lived surface.
         */
        staleTime: 4 * 60_000,
        refetchOnWindowFocus: true,
    });
}
//# sourceMappingURL=journey.js.map